import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Sparkles, ThumbsUp, ThumbsDown } from 'lucide-react';

/**
 * SmartReflectionCard — the short note shown once after the first substantive
 * save of a journal day.
 *
 * The only surface in the app that exists purely to be read. There is nothing
 * to choose and nothing to do, so it takes the middle of a quieted screen and
 * waits: dismissible by ✕, Escape, or the backdrop, and by nothing else. No
 * auto-hide — someone re-reading a sentence about their own day should never
 * have it pulled out from under them.
 *
 * Rendered as a portal so it can't be clipped by the journal's layout, and it
 * drops to a plain fade under prefers-reduced-motion.
 */

const AMBER = '#D4A574';
const INK = '#2D2A27';

/**
 * How wide the card gets and how large its text sets, chosen from the length of
 * what the model actually wrote.
 *
 * A fixed card has to be sized for the longest reflection it might ever hold,
 * which leaves a one-line note marooned in a large empty box. So the card is cut
 * to the text: short reflections get a narrow card and larger type, and it
 * tightens as the writing runs longer. Kept in step with the Flutter card's
 * _CardMetrics so the two platforms don't drift apart.
 *
 * Three buckets rather than a continuous function — predictable sizes, stable
 * layout, and one integer comparison instead of a measuring pass.
 */
function cardMetrics(text) {
  const t = (text || '').trim();
  const words = t ? t.split(/\s+/).length : 0;
  if (words <= 20) return { maxWidth: 340, fontSize: '1.2rem' };
  if (words <= 45) return { maxWidth: 400, fontSize: '1.08rem' };
  return { maxWidth: 450, fontSize: '0.99rem' };
}

export default function SmartReflectionCard({ open, reflection, invitation, feedback, onFeedback, onClose }) {
  const closeRef = useRef(null);
  const returnFocusRef = useRef(null);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Remember whatever had focus (the Save button, normally) and hand it back on
  // close, so a keyboard user is returned to where they were rather than to the
  // top of the document.
  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    closeRef.current?.focus();
    return () => {
      const target = returnFocusRef.current;
      if (target && typeof target.focus === 'function') target.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  // Lock the page behind the card. Without this, iOS Safari scrolls the journal
  // underneath while the card sits still, which reads as a rendering bug.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open || !reflection) return null;

  const metrics = cardMetrics(reflection);

  const modal = (
    <div
      className="src-root fixed inset-0 z-[120] flex items-center justify-center p-5"
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <style>{`
        .src-root { background: rgba(24, 20, 17, 0.55); animation: src-veil 240ms ease both; }
        .src-card { animation: src-rise 300ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes src-veil { from { opacity: 0; } to { opacity: 1; } }
        @keyframes src-rise { from { opacity: 0; transform: translateY(10px) scale(0.96); } to { opacity: 1; transform: none; } }
        @keyframes src-fade { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          .src-root, .src-card { animation: src-fade 180ms ease both; }
        }
      `}</style>

      <div
        role="dialog"
        aria-modal="true"
        aria-label="A reflection on today's entry"
        className="src-card relative w-full rounded-[2rem] px-7 pb-8 pt-7 text-center"
        style={{
          backgroundColor: INK,
          boxShadow: '0 24px 60px -18px rgba(20, 16, 12, 0.6)',
          maxWidth: `${metrics.maxWidth}px`,
          maxHeight: 'calc(100vh - 40px)',
          overflowY: 'auto',
        }}
      >
        {/* Out of the flow, not above it. As a flex row the ✕ reserved its own
            band across the top of the card, so every reflection opened with a
            block of nothing before the sparkle. It sits in the corner instead,
            and the card's top padding now matches its sides. */}
        <button
          ref={closeRef}
          type="button"
          onClick={handleClose}
          aria-label="Close reflection"
          className="absolute right-3 top-3 rounded-full p-2 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2"
          style={{ color: '#F0EDE8', opacity: 0.55 }}
        >
          <X size={18} strokeWidth={2} />
        </button>

        {/* Two concentric tints rather than a solid disc — a glow on the dark
            card, not a badge stuck to it. */}
        <div
          aria-hidden="true"
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full"
          style={{ backgroundColor: 'rgba(212, 165, 116, 0.14)' }}
        >
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: 'rgba(212, 165, 116, 0.16)' }}
          >
            <Sparkles size={22} color={AMBER} strokeWidth={1.75} />
          </div>
        </div>

        <p
          className="mt-5 font-serif leading-[1.62]"
          style={{ color: '#F5F2EE', fontSize: metrics.fontSize }}
        >
          {reflection}
        </p>

        {invitation ? (
          <>
            {/* A hairline, not a rule: the invitation is a turn in the same
                thought, not a second section. */}
            <div
              aria-hidden="true"
              className="mx-auto mt-6 h-px w-7"
              style={{ backgroundColor: 'rgba(240, 237, 232, 0.22)' }}
            />
            <p
              className="mt-4 text-[0.8rem] leading-relaxed tracking-wide"
              style={{ color: AMBER }}
            >
              {invitation}
            </p>
          </>
        ) : null}

        {/* Quiet quality signal, not a call to action — this is the one thing
            in the card that isn't purely for reading. No labels, no counts,
            just two small toggles that fade in with everything else. */}
        {onFeedback ? (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => onFeedback(feedback === 'like' ? 'clear' : 'like')}
              aria-label={feedback === 'like' ? 'Remove like' : 'This reflection felt right'}
              aria-pressed={feedback === 'like'}
              className="rounded-full p-2 transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2"
              style={{
                color: feedback === 'like' ? AMBER : '#F0EDE8',
                opacity: feedback === 'like' ? 1 : 0.45,
                backgroundColor: feedback === 'like' ? 'rgba(212, 165, 116, 0.14)' : 'transparent',
              }}
            >
              <ThumbsUp size={16} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => onFeedback(feedback === 'dislike' ? 'clear' : 'dislike')}
              aria-label={feedback === 'dislike' ? 'Remove dislike' : 'This reflection missed the mark'}
              aria-pressed={feedback === 'dislike'}
              className="rounded-full p-2 transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2"
              style={{
                // Deliberately not a warning red — this app never uses alarm
                // colors on the user's own reflection, just the same neutral
                // off-white the card's text already uses, brightened on select.
                color: '#F0EDE8',
                opacity: feedback === 'dislike' ? 1 : 0.45,
                backgroundColor: feedback === 'dislike' ? 'rgba(240, 237, 232, 0.14)' : 'transparent',
              }}
            >
              <ThumbsDown size={16} strokeWidth={2} />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
